// @ts-nocheck
// #region ==================== IMPORTS ====================
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FilterList, Sort, ViewColumn } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import StarIcon from '@mui/icons-material/Star';
import UpdateIcon from '@mui/icons-material/Update';
import {
  MenuItem,
  Select,
  Popover,
  Tooltip,
  FormControl,
  InputLabel,
  Checkbox,
  Stack,
  Typography,
  Divider,
  Box,
  FormGroup,
  FormControlLabel,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AtomButton from '@/components/atoms/Button';
import AtomCustomBadge from '@/components/atoms/CustomBadge';
import AtomTextField from '@/components/atoms/TextField';
import OrgActionMenu from '@/components/molecules/ActionMenu';
import OrgDataTable from '@/components/organisms/DataTable';
import OrgDialog from '@/components/organisms/Dialog';
import companyStyles from '@/pages/Company/company.module.css';
import { AppDispatch } from '@/store';
import { selectUser } from '@/store/auth';
import { companies } from '@/store/companies/thunks';
import {
  getCompanyData,
  getStudyProfile,
  getStudyName,
  getYearSelected,
} from '@/store/studies/selectors';
import { setYearSelected } from '@/store/studies/slice';
import {
  selectStudyYearCompanies,
  selectStudyYearsLoading,
  selectStudyYearsError,
} from '@/store/study-year/selectors';
import { studyYears } from '@/store/study-year/thunks';
import { FileService } from '@/utils/file';
import { CompanyDialogContent } from './Actions/CompanyDialogContent';
import CompanyAnalysisLoadingState from './CompanyAnalysisLoadingState';

// #endregion

const CompanyAnalysis: React.FC = () => {
  // #region ==================== DISPATCH ====================
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  // #endregion

  // #region ==================== STATES ====================
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortBy, setSortBy] = useState('revenue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);
  const [columnsSelected, setColumnsSelected] = useState<string[]>([
    'company_name',
    'revenue',
    'revenue_status',
    'yoy_revenue_percentage',
    'market_share',
  ]);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [isCompanyActionDialogOpen, setIsCompanyActionDialogOpen] = useState({
    isOpen: false,
    action: null,
    company: null,
  });
  const [isAddCompanyDialogOpen, setIsAddCompanyDialogOpen] = useState(false);
  const [newCompanyForm, setNewCompanyForm] = useState({
    company_name: '',
    revenue: '',
    units_sold: '',
    selling_price: '',
    revenue_status: 'modelled',
    supplier_status: 'active',
    // note: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [availableCompanies, setAvailableCompanies] = useState<CompanySchema[]>([]);
  const [companySearchLoading, setCompanySearchLoading] = useState(false);
  const [companySearchTimeout, setCompanySearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedCompanyObject, setSelectedCompanyObject] = useState<CompanySchema | null>(null);

  // #endregion

  // #region ================== SELECTORS ================
  const useStudyProfile = useSelector(getStudyProfile);
  const useYearSelected = useSelector(getYearSelected);
  const useCompanyData = useSelector(getCompanyData);
  const useStudyName = useSelector(getStudyName);
  const useCompanyDataV2 = useSelector(selectStudyYearCompanies);
  const isAddingCompany = useSelector(selectStudyYearsLoading);
  const addCompanyError = useSelector(selectStudyYearsError);
  const useUser = useSelector(selectUser);

  // #endregion

  // #region ================== VARIABLE ================
  // -------------------- CONSTANTS & UTILITIES --------------------

  const getColorScale = (value: number, min: number, max: number): string => {
    const colors = [
      '#d32f2f', // deep red
      '#e53935', // bright red
      '#f44336', // standard red
      '#ff5722', // deep orange
      '#ff9800', // orange
      '#ffc107', // amber
      '#4caf50', // standard green
      '#43a047', // light green
      '#388e3c', // medium green
      '#2e7d32', // dark green
    ];

    if (value === min) return colors[0];
    if (value === max) return colors[colors.length - 1];

    const normalized = (value - min) / (max - min);
    const index = Math.floor(normalized * (colors.length - 1));
    return colors[Math.min(index, colors.length - 1)];
  };

  const getGradientClass = (value: number, minChange: number, maxChange: number): string => {
    if (value === null) return '';

    const normalized = (value - minChange) / (maxChange - minChange);
    const index = Math.floor(normalized * 9); // 10 classes (0-9)
    return `gradient-${Math.min(Math.max(index, 0), 9)}`;
  };

  const getColumnLabel = (key: string): string => {
    const labelMap: Record<string, string> = {
      revenue: 'Revenue',
      yoy_revenue: 'YoY Revenue',
      yoy_revenue_percentage: 'YoY Revenue %',
      units_sold: 'Units Sold',
      selling_price: 'Selling Price',
      yoy_units: 'YoY Units',
      yoy_units_percentage: 'YoY Units %',
      yoy_selling_price: 'YoY Selling Price',
      yoy_selling_price_percent: 'YoY Selling Price %',
      supplier_status: 'Supplier Status',
      revenue_status: 'Revenue Status',
      market_share: 'Market Share',
      avg_yoy_financials_growth: 'Financials Growth',
      yoy_market_share: 'YoY Market Share',
      py_yoy_revenue_percentage: 'Previous Year YoY %',
      py_revenue: 'Previous Year Revenue',
      rank_revenue: 'Revenue Rank',
      yoy_rank_change: 'Rank Change',
      time_updated: 'Last Updated',
    };

    return labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // -------------------- FILTER HELPERS --------------------
  const getFilterInputType = (columnKey: string) => {
    const numberColumns = [
      'revenue',
      'yoy_revenue',
      'py_revenue',
      'selling_price',
      'yoy_selling_price',
      'units_sold',
      'yoy_units',
      'market_share',
      'yoy_market_share',
      'yoy_revenue_percentage',
      'py_yoy_revenue_percentage',
      'avg_yoy_financials_growth',
      'yoy_units_percentage',
      'yoy_selling_price_percent',
      'rank_revenue',
      'yoy_rank_change',
    ];

    const dateColumns = ['time_updated', 'time_created'];

    if (numberColumns.includes(columnKey)) return 'number';
    if (dateColumns.includes(columnKey)) return 'date';
    return 'text';
  };

  const renderFilterInput = (columnKey: string) => {
    const inputType = getFilterInputType(columnKey);
    const label = getColumnLabel(columnKey);

    const getPlaceholder = () => {
      switch (inputType) {
        case 'number':
          return `Enter ${label.toLowerCase()} value...`;
        case 'date':
          return 'YYYY-MM-DD';
        default:
          return `Filter ${label.toLowerCase()}...`;
      }
    };

    return (
      <input
        type={inputType}
        placeholder={getPlaceholder()}
        value={filters[columnKey] || ''}
        onChange={e => handleFilterChange(columnKey, e.target.value)}
        className={companyStyles.filterInput}
        style={{
          width: '100%',
          padding: '8px 12px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          fontSize: '14px',
        }}
      />
    );
  };

  const minChange = Math.min(...useCompanyData.map(d => d.year_over_year_change));
  const maxChange = Math.max(...useCompanyData.map(d => d.year_over_year_change));

  // -------------------- MENU CONFIGURATIONS --------------------

  const companyMenuItems = useMemo(
    () => [
      {
        label: 'Edit',
        onClick: row => openCompanyActionDialog({ isOpen: true, action: 'edit', company: row }),
      },
      {
        label: 'Edit Segments',
        onClick: row =>
          openCompanyActionDialog({ isOpen: true, action: 'editsegments', company: row }),
      },
      {
        label: 'Financials',
        onClick: row =>
          openCompanyActionDialog({ isOpen: true, action: 'financials', company: row }),
      },
      {
        label: 'History',
        onClick: row =>
          openCompanyActionDialog({ isOpen: true, action: 'seehistory', company: row }),
      },
      {
        label: 'Track Changes',
        onClick: row =>
          openCompanyActionDialog({ isOpen: true, action: 'seeversions', company: row }),
      },
      {
        label: 'Schedule Briefing',
        onClick: row =>
          openCompanyActionDialog({ isOpen: true, action: 'schedulebriefing', company: row }),
      },
      {
        label: 'Contacts',
        onClick: row => openCompanyActionDialog({ isOpen: true, action: 'contacts', company: row }),
      },
      {
        label: 'Download Survey',
        onClick: row => handleDownload(row, useStudyName),
      },
      {
        label: 'Upload Survey',
        onClick: row =>
          openCompanyActionDialog({ isOpen: true, action: 'uploadsurvey', company: row }),
      },
      // {
      //   label: 'Send Survey',
      //   onClick: row => openCompanyActionDialog({ isOpen: true, action: 'sendsurvey', company: row }),
      // },
    ],
    [useStudyName]
  );

  // #endregion

  // #region ================== MEMO ================

  // -------------------- DATA PROCESSING --------------------
  const enrichedCompanyData = useMemo(() => {
    return useCompanyDataV2.map(company => ({
      ...company,
      // Ensure all sortable fields exist
      company_name: company.company_name || company.name,
      revenue: company.revenue || 0,
      revenue_status: company.revenue_status || '',
      yoy_revenue_percentage: company.yoy_revenue_percentage || 0,
      market_share: company.market_share || 0,
      py_revenue: company.py_revenue || 0,
      py_yoy_revenue_percentage: company.py_yoy_revenue_percentage || 0,
      yoy_market_share: company.yoy_market_share || 0,
      avg_yoy_financials_growth: company.avg_yoy_financials_growth || 0,
      units_sold: company.units_sold || 0,
      yoy_units_percentage: company.yoy_units_percentage || 0,
      selling_price: company.selling_price || 0,
    }));
  }, [useCompanyDataV2]);

  // -------------------- FILTERING & SORTING --------------------
  const processedData = useMemo(() => {
    let result = [...enrichedCompanyData];

    // Apply filters with smart type handling
    if (Object.keys(filters).length > 0) {
      result = result.filter(row => {
        return Object.entries(filters).every(([key, value]) => {
          if (!value) return true;

          const cellValue = row[key];
          const filterType = getFilterInputType(key);

          switch (filterType) {
            case 'number':
              // For numbers, support exact match or range
              const numericCellValue = Number(cellValue);
              const numericFilterValue = Number(value);

              if (isNaN(numericFilterValue)) {
                // If not a number, fall back to string search
                return String(cellValue || '')
                  .toLowerCase()
                  .includes(String(value).toLowerCase());
              }

              // Exact number match or close approximation
              return Math.abs(numericCellValue - numericFilterValue) < 0.01;

            case 'date':
              // For dates, convert to comparable format
              if (!cellValue) return false;
              const cellDate = new Date(cellValue).toISOString().split('T')[0];
              return cellDate.includes(value);

            default:
              // Text search (case insensitive)
              return String(cellValue || '')
                .toLowerCase()
                .includes(String(value).toLowerCase());
          }
        });
      });
    }

    // Apply sorting
    if (sortBy) {
      result.sort((a, b) => {
        const aValue = a[sortBy];
        const bValue = b[sortBy];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
        }

        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();
        return sortDirection === 'asc'
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      });
    }

    return result;
  }, [enrichedCompanyData, filters, sortBy, sortDirection]);

  // -------------------- PAGINATION --------------------
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return processedData.slice(startIndex, startIndex + rowsPerPage);
  }, [processedData, page, rowsPerPage]);

  // -------------------- DYNAMIC COLUMNS --------------------
  const dynamicColumnOptions = useMemo(() => {
    if (!useCompanyDataV2 || useCompanyDataV2.length === 0) {
      return [];
    }

    // Get all unique keys from the first item
    const firstItem = useCompanyDataV2[0];
    const allKeys = Object.keys(firstItem);

    // Filter out keys that shouldn't be selectable columns
    const excludedKeys = [
      'id',
      'company_id',
      'study_id',
      'previous_year_id',
      'time_created',
      'last_edited_by',
      'study_year_company_id',
      'study_year_id',
    ];

    const essentialColumns = ['company_name', 'action'];

    return allKeys
      .filter(key => !excludedKeys.includes(key) && !essentialColumns.includes(key))
      .sort();
  }, [useCompanyDataV2]);

  const filteredColumns = useMemo(() => {
    const columnsV2 = [
      {
        id: 'company_name',
        label: 'Company',
        flex: 2.5,
      },
      {
        id: 'revenue',
        label: 'Revenue',
        flex: 0.8,
        renderCell: (params: GridRenderCellParams) => {
          const fullRowData = params.row;
          const formattedValue = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(params.value);

          const tooltipContent = (
            <div>
              <div>
                Last Updated:
                {fullRowData.time_updated
                  ? new Date(fullRowData?.time_updated.substring(0, 23)).toLocaleString()
                  : '-'}
              </div>
              <div>
                Updated By:{' '}
                {fullRowData.last_edited_by
                  ? `${fullRowData.last_edited_by.first_name} ${fullRowData.last_edited_by.last_name}`
                  : 'No data found'}
              </div>
            </div>
          );

          return (
            <Tooltip title={tooltipContent} arrow>
              <div>{formattedValue}</div>
            </Tooltip>
          );
        },
        sortable: true,
      },
      {
        id: 'revenue_status',
        label: 'Status',
        flex: 1.3,
        renderCell: params => {
          const getStatusIcon = (status: string) => {
            switch (status.toLowerCase()) {
              case 'modelled':
                return <CurrencyExchangeIcon fontSize="small" />;
              case 'revised':
                return <UpdateIcon fontSize="small" />;
              case 'override':
                return <CheckCircleOutlineIcon fontSize="small" />;
              case 'supplier input':
                return <StarIcon fontSize="small" />;
              default:
                return null;
            }
          };
          return (
            <AtomCustomBadge
              variant={params.value.toLowerCase()}
              label={params.value}
              icon={getStatusIcon(params.value)}
              size="md"
            />
          );
        },
      },
      {
        id: 'yoy_revenue_percentage',
        label: 'Revenue YoY',
        flex: 0.8,
        valueFormatter: params => `${(params * 100).toFixed(2)}%`,
        sortable: true,
      },
      {
        id: 'market_share',
        label: 'Market Share',
        valueFormatter: params => `${(params * 100).toFixed(2)}%`,
        sortable: true,
      },
      {
        id: 'py_revenue',
        label: 'Previous Year Revenue',
        valueFormatter: params => {
          const formattedValue = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(params);
          return formattedValue;
        },
      },
      {
        id: 'py_yoy_revenue_percentage',
        label: 'Previous Year YoY',
        valueFormatter: params => `${(params * 100).toFixed(2)}%`,
      },
      {
        id: 'yoy_market_share',
        label: 'Market Share YoY',
        valueFormatter: params => `${(params * 100).toFixed(2)}%`,
      },
      {
        id: 'avg_yoy_financials_growth',
        label: 'Financials Growth Index',
        valueFormatter: params => `${(params * 100).toFixed(2)}%`,
      },
      {
        id: 'time_updated',
        label: 'Time Updated',
        type: 'date',
        valueFormatter: params => {
          if (!params) return '';
          try {
            // Handle microseconds by converting to milliseconds
            let dateStr = params;
            if (dateStr.includes('.') && !dateStr.endsWith('Z')) {
              // If it has microseconds, trim to milliseconds
              dateStr = dateStr.replace(/(\.\d{3})\d*/, '$1');
            }
            const date = new Date(dateStr);
            return isNaN(date.getTime()) ? params : date.toLocaleString();
          } catch (error) {
            console.log(params);
            return params;
          }
        },
      },
      {
        id: 'units_sold',
        label: 'Units Sold',
      },
      {
        id: 'yoy_units_percentage',
        label: 'Units Sold YoY',
        valueFormatter: params => `${(params * 100).toFixed(2)}%`,
      },
      {
        id: 'selling_price',
        label: 'Selling Price',
      },
      // {
      //   id: 'yoy_rank_change',
      //   label: 'YOY Rank Change',
      // },
      {
        id: 'rank_revenue',
        label: 'Revenue Rank',
        flex: 0.8,
        sortable: true,
        valueFormatter: (params: any) => {
          return params || '-';
        },
      },
      {
        id: 'yoy_rank_change',
        label: 'Rank Change',
        flex: 0.8,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params === null || params === undefined) return '-';
          return params > 0 ? `+${params}` : `${params}`;
        },
        renderCell: (params: any) => {
          const value = params.value;
          if (value === null || value === undefined) return '-';

          const color = value > 0 ? '#d32f2f' : value < 0 ? '#2e7d32' : '#666';
          const symbol = value > 0 ? '↓' : value < 0 ? '↑' : '';

          return (
            <div style={{ color, fontWeight: 'bold' }}>
              {symbol} {Math.abs(value)}
            </div>
          );
        },
      },
      {
        id: 'supplier_status',
        label: 'Supplier Status',
        flex: 1.2,
        sortable: true,
        renderCell: (params: any) => (
          <AtomCustomBadge
            variant={params.value?.toLowerCase() || 'default'}
            label={params.value || 'Unknown'}
            size="md"
          />
        ),
      },
      {
        id: 'yoy_revenue',
        label: 'YoY Revenue',
        flex: 0.8,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params === null || params === undefined) return '-';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(params);
        },
      },
      {
        id: 'yoy_units',
        label: 'YoY Units',
        flex: 0.8,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params === null || params === undefined) return '-';
          return new Intl.NumberFormat('en-US').format(params);
        },
      },
      {
        id: 'yoy_selling_price',
        label: 'YoY Selling Price',
        flex: 0.8,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params === null || params === undefined) return '-';
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(params);
        },
      },
      {
        id: 'yoy_selling_price_percent',
        label: 'YoY Selling Price %',
        flex: 0.8,
        sortable: true,
        valueFormatter: (params: any) => {
          if (params === null || params === undefined) return '-';
          return `${(params * 100).toFixed(2)}%`;
        },
      },
      {
        id: 'action',
        label: '',
        sortable: false,
        flex: 0.6,
        maxWidth: 60,
        renderCell: params => (
          <OrgActionMenu
            menuItems={companyMenuItems}
            rowData={{ ...params, fullRowData: useCompanyData.find(row => row.id === params.id) }}
            rowId={params.id}
            // className={segmentStyles.action}
          />
        ),
      },
    ];

    const essentialColumns = ['company_name', 'action'];
    return columnsV2.filter(
      col => columnsSelected.includes(col.id) || essentialColumns.includes(col.id)
    );
  }, [columnsSelected, companyMenuItems, useCompanyData]);

  // #endregion

  // #region ================== FUNCTIONS ================

  // Add these functions in your FUNCTIONS section

  // Form handlers
  const handleAddCompanyDialogOpen = () => {
    setIsAddCompanyDialogOpen(true);
    setFormErrors({});
  };

  const handleAddCompanyDialogClose = () => {
    setIsAddCompanyDialogOpen(false);
    setNewCompanyForm({
      company_name: '',
      revenue: '',
      units_sold: '',
      selling_price: '',
      revenue_status: 'modelled',
      supplier_status: 'active',
      // note: '',
    });
    setFormErrors({});
    setAvailableCompanies([]);
    setSelectedCompanyObject(null);

    if (companySearchTimeout) {
      clearTimeout(companySearchTimeout);
      setCompanySearchTimeout(null);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setNewCompanyForm(prev => ({
      ...prev,
      [field]: value,
    }));

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!newCompanyForm.company_name.trim()) {
      errors.company_name = 'Company name is required';
    }

    if (newCompanyForm.revenue && Number(newCompanyForm.revenue) < 0) {
      errors.revenue = 'Revenue cannot be negative';
    }

    if (newCompanyForm.units_sold && Number(newCompanyForm.units_sold) < 0) {
      errors.units_sold = 'Units sold cannot be negative';
    }

    if (newCompanyForm.selling_price && Number(newCompanyForm.selling_price) < 0) {
      errors.selling_price = 'Selling price cannot be negative';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddCompanySubmit = async () => {
    if (!validateForm()) return;

    try {
      const studyYearId = `${useYearSelected.study_year_id}`;

      let companyId;
      if (selectedCompanyObject) {
        companyId = selectedCompanyObject.id;
      } else {
        companyId = `company_${Date.now()}`;
      }

      const request: AddStudyYearCompanyRequest = {
        user_id: useUser.id,
        company_id: companyId,
        revenue: newCompanyForm.revenue ? Number(newCompanyForm.revenue) : 0,
        units_sold: newCompanyForm.units_sold ? Number(newCompanyForm.units_sold) : 0,
        revenue_status: newCompanyForm.revenue_status,
        supplier_status: newCompanyForm.supplier_status,
      };

      await dispatch(
        studyYears.addStudyYearCompany({
          studyYearId,
          request,
        })
      );

      // Refresh company data after successful addition
      await fetchCompanyData();

      // Close dialog
      handleAddCompanyDialogClose();
    } catch (error) {
      console.error('Error adding company:', error);
    }
  };

  // -------------------- ACTION HANDLERS --------------------
  const handleDownload = async row => {
    try {
      const fileService = new FileService();

      const response = await fileService.downloadFile(
        `study-year-companies/${row.row.study_year_company_id}/download-survey`
      );

      if (!response.blob) {
        throw new Error('No file data received');
      }

      // Convert binary string to bytes
      const bytes = new Uint8Array(response.blob.length);
      for (let i = 0; i < response.blob.length; i++) {
        bytes[i] = response.blob.charCodeAt(i);
      }

      // Create Excel blob
      const blob = new Blob([bytes], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      // Set filename
      const filename = response.filename || `survey-${row.row.study_year_company_id}.xlsx`;
      const finalFilename = filename.endsWith('.xlsx') ? filename : filename + '.xlsx';

      // Download file
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalFilename;
      link.click();

      // Cleanup
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert(`Download failed: ${error.message}`);
    }
  };

  const handleYearChange = async event => {
    const newYearId = event.target.value;
    const selectedYearData = useStudyProfile?.years_available_data?.find(
      yearItem => yearItem.study_year_id === newYearId
    );
    if (selectedYearData) {
      dispatch(setYearSelected(selectedYearData));
    }
  };

  // -------------------- PAGINATION HANDLERS --------------------
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // -------------------- FILTER HANDLERS --------------------
  const handleFilterChange = (columnId: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [columnId]: value,
    }));
    setPage(0);
  };

  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  // -------------------- MENU HANDLERS (New) --------------------
  const handleSortMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation(); // Add this
    setSortMenuAnchor(event.currentTarget);
  };

  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };

  const handleSortItemClick = (columnId: string) => {
    const newDirection = sortBy === columnId && sortDirection === 'asc' ? 'desc' : 'asc';
    handleSortSelect({ field: columnId, sort: newDirection });
    handleSortMenuClose(); // Explicitly close the menu
  };

  const handleSortSelect = (sortModel: { field: string; sort: 'asc' | 'desc' | undefined }) => {
    setSortBy(sortModel.field);
    setSortDirection(sortModel.sort || 'asc');
    handleSortMenuClose();
  };

  // -------------------- COLUMN HANDLERS --------------------
  const handleColumnMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnMenuAnchor(event.currentTarget);
  };

  const handleColumnMenuClose = () => {
    setColumnMenuAnchor(null);
  };

  const handleColumnToggle = (columnId: string) => {
    setColumnsSelected(prev => {
      if (prev.includes(columnId)) {
        return prev.filter(id => id !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  const handleSelectAllColumns = () => {
    setColumnsSelected(['company_name', ...dynamicColumnOptions]);
  };

  const handleResetColumns = () => {
    setColumnsSelected([
      'company_name',
      'revenue',
      'revenue_status',
      'yoy_revenue_percentage',
      'market_share',
    ]);
  };

  // -------------------- DIALOG HANDLERS --------------------

  const openCompanyActionDialog = arg => {
    setIsCompanyActionDialogOpen(arg);
  };

  const closeCompanyActionDialog = () => {
    setIsCompanyActionDialogOpen({ isOpen: false, action: null, company: null });
  };
  // #endregion

  // #region ================== API CALLS ================
  const fetchCompanyData = useCallback(async () => {
    setIsLoading(true);
    try {
      await dispatch(studyYears.getStudyYearCompanies(useYearSelected.study_year_id));
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, useYearSelected]);

  const fetchAvailableCompanies = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 1) {
        setAvailableCompanies([]);
        return;
      }

      setCompanySearchLoading(true);

      try {
        const params = {
          name__like: searchTerm,
          limit: 20, // Limit results for autocomplete
          offset: 0,
        };

        // Use your existing companies API
        const response = await dispatch(companies.getCompanies(params));

        // Handle different possible response structures
        let companiesData = [];
        if (response.payload) {
          // If payload is an array, use it directly
          if (Array.isArray(response.payload)) {
            companiesData = response.payload;
          }
          // If payload has a data property that's an array
          else if (response.payload.data && Array.isArray(response.payload.data)) {
            companiesData = response.payload.data;
          }
          // If payload has a results property that's an array
          else if (response.payload.results && Array.isArray(response.payload.results)) {
            companiesData = response.payload.results;
          }
        }

        setAvailableCompanies(companiesData);
      } catch (error) {
        console.error('Error fetching companies for autocomplete:', error);
        setAvailableCompanies([]);
      } finally {
        setCompanySearchLoading(false);
      }
    },
    [dispatch]
  );

  // #endregion

  // #region ================== EFFECTS ================
  useEffect(() => {
    if (useYearSelected) {
      const fetchData = async () => {
        await fetchCompanyData();
      };

      fetchData();
    }
  }, [useYearSelected, fetchCompanyData]);

  useEffect(() => {
    return () => {
      // Cleanup timeout on component unmount
      if (companySearchTimeout) {
        clearTimeout(companySearchTimeout);
      }
    };
  }, [companySearchTimeout]);

  // #endregion

  const handleCompanySearchChange = useCallback(
    (searchValue: string) => {
      // Clear existing timeout
      if (companySearchTimeout) {
        clearTimeout(companySearchTimeout);
      }

      // Set new timeout for debounced search
      const timeout = setTimeout(() => {
        fetchAvailableCompanies(searchValue);
      }, 300); // 300ms delay

      setCompanySearchTimeout(timeout);
    },
    [fetchAvailableCompanies, companySearchTimeout]
  );

  return (
    <div className="container">
      {isLoading ? (
        <CompanyAnalysisLoadingState studyName={useStudyName} rowCount={8} />
      ) : (
        <div className={companyStyles.container}>
          <div className={companyStyles.headContainer}>
            <h1 className={companyStyles.header}>{useStudyName}</h1>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel id="year-select-label">Year</InputLabel>
              <Select
                labelId="year-select-label"
                id="year-select"
                value={useYearSelected?.study_year_id || ''} // Extract study_year_id from the object
                label="Year"
                onChange={handleYearChange}
              >
                {useStudyProfile?.years_available_data
                  ?.slice()
                  ?.sort((a, b) => b.year - a.year)
                  ?.map(yearItem => (
                    <MenuItem key={yearItem.study_year_id} value={yearItem.study_year_id}>
                      {yearItem.year}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </div>

          {/* Data Table */}
          <div className={companyStyles.options}>
            <h3>Company Details</h3>
            <Stack direction="row" marginTop={2} spacing={1} sx={{ mb: 2 }}>
              <AtomButton
                size="small"
                onClick={handleSortMenuOpen}
                startIcon={<Sort />}
                variant="text"
                sx={{ mr: 1 }}
              >
                Sort By
              </AtomButton>
              <AtomButton
                size="small"
                onClick={handleFilterMenuOpen}
                startIcon={<FilterList />}
                variant="text"
              >
                Filter
              </AtomButton>
              <AtomButton
                size="small"
                onClick={handleColumnMenuOpen}
                startIcon={<ViewColumn />}
                variant="text"
              >
                Column
              </AtomButton>
              <AtomButton
                variant="contained"
                color="primary"
                onClick={handleAddCompanyDialogOpen}
                size="small"
              >
                Add Company
              </AtomButton>
            </Stack>

            {/* Sort Menu */}
            <Popover
              open={Boolean(sortMenuAnchor)}
              anchorEl={sortMenuAnchor}
              onClose={handleSortMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              sx={{ mt: 1 }}
            >
              <div style={{ padding: '8px 0', minWidth: '200px' }}>
                <div
                  style={{
                    padding: '8px 16px',
                    fontWeight: 'bold',
                    borderBottom: '1px solid #eee',
                  }}
                >
                  Sort By
                </div>
                {filteredColumns
                  .filter(
                    col =>
                      col.sortable !== false &&
                      !col.id.includes('action') &&
                      col.id !== 'company_name'
                  )
                  .map(column => (
                    <div
                      key={column.id}
                      onClick={() => handleSortItemClick(column.id)}
                      style={{
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        ':hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {column.label}
                      {sortBy === column.id && (
                        <span style={{ marginLeft: 8 }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  ))}
              </div>
            </Popover>

            {/* Filter Menu */}
            <Popover
              open={Boolean(filterMenuAnchor)}
              anchorEl={filterMenuAnchor}
              onClose={handleFilterMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{ mt: 1 }}
            >
              <Box sx={{ p: 2, width: 350 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Filter Columns
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={2}>
                  Filter data by any column values
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {/* Company Name Filter */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={500} mb={1}>
                      Company Name
                    </Typography>
                    <input
                      type="text"
                      placeholder="Filter company name..."
                      value={filters['company_name'] || ''}
                      onChange={e => handleFilterChange('company_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px',
                      }}
                    />
                  </Box>

                  {/* Dynamic Filters for all API columns */}
                  {dynamicColumnOptions.map(columnKey => (
                    <Box key={columnKey} sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={500} mb={1}>
                        {getColumnLabel(columnKey)}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          ml={1}
                        >
                          ({getFilterInputType(columnKey)})
                        </Typography>
                      </Typography>
                      {renderFilterInput(columnKey)}
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 2 }} />
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="caption" color="text.secondary">
                    {Object.keys(filters).filter(key => filters[key]).length} active filters
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <AtomButton
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setFilters({});
                        setPage(0);
                      }}
                    >
                      Clear All
                    </AtomButton>
                    <AtomButton size="small" variant="contained" onClick={handleFilterMenuClose}>
                      Apply
                    </AtomButton>
                  </Stack>
                </Stack>
              </Box>
            </Popover>

            {/* Coloumn Menu */}
            <Popover
              open={Boolean(columnMenuAnchor)}
              anchorEl={columnMenuAnchor}
              onClose={handleColumnMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              sx={{ mt: 1 }}
            >
              <Box sx={{ p: 2, width: 300 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                  Select Columns
                </Typography>
                <Typography variant="caption" color="text.secondary" mb={2}>
                  Choose which columns to display in the table
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  <FormGroup>
                    {/* Company Name - Always visible, disabled */}
                    <FormControlLabel
                      control={<Checkbox checked={true} disabled={true} size="small" />}
                      label="Company Name (Required)"
                    />

                    {/* Dynamic columns from API */}
                    {dynamicColumnOptions.map(option => (
                      <FormControlLabel
                        key={option}
                        control={
                          <Checkbox
                            checked={columnsSelected.includes(option)}
                            onChange={() => handleColumnToggle(option)}
                            size="small"
                          />
                        }
                        label={getColumnLabel(option)}
                      />
                    ))}
                  </FormGroup>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" spacing={1} justifyContent="space-between">
                  <Typography variant="caption" color="text.secondary">
                    {columnsSelected.length} of {dynamicColumnOptions.length + 1} selected
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    <AtomButton size="small" variant="outlined" onClick={handleSelectAllColumns}>
                      Select All
                    </AtomButton>
                    <AtomButton size="small" variant="outlined" onClick={handleResetColumns}>
                      Reset
                    </AtomButton>
                  </Stack>
                </Stack>
              </Box>
            </Popover>
          </div>

          <OrgDataTable
            columns={filteredColumns}
            data={paginatedData}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={processedData.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSortSelect}
            disableColumnResize={false}
            disableColumnMenu={true}
            enableZebraStripes={true}
          />

          <OrgDialog
            open={isCompanyActionDialogOpen.isOpen}
            onClose={closeCompanyActionDialog}
            title={isCompanyActionDialogOpen.company?.row.company_name}
            maxWidth="md"
          >
            {isCompanyActionDialogOpen.action && isCompanyActionDialogOpen.company && (
              <CompanyDialogContent
                action={isCompanyActionDialogOpen.action}
                company={isCompanyActionDialogOpen.company}
                onClose={closeCompanyActionDialog}
              />
            )}
          </OrgDialog>

          {/* Add Company Dialog */}
          <OrgDialog
            open={isAddCompanyDialogOpen}
            onClose={handleAddCompanyDialogClose}
            title="Add New Company"
            maxWidth="md"
            actions={
              <Stack direction="row" spacing={2}>
                <AtomButton
                  variant="outlined"
                  onClick={handleAddCompanyDialogClose}
                  disabled={isAddingCompany}
                >
                  Cancel
                </AtomButton>
                <AtomButton
                  variant="contained"
                  onClick={handleAddCompanySubmit}
                  loading={isAddingCompany}
                >
                  Add Company
                </AtomButton>
              </Stack>
            }
          >
            <div>
              <div className={companyStyles.addCompanyForm}>
                <div className={companyStyles.formRow}>
                  <Autocomplete
                    className={companyStyles.companydrop}
                    freeSolo
                    options={availableCompanies}
                    getOptionLabel={option => {
                      // Handle both string and object options
                      if (typeof option === 'string') return option;
                      return option.company_name || option.name || '';
                    }}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof option === 'string' && typeof value === 'string') {
                        return option === value;
                      }
                      if (typeof option === 'object' && typeof value === 'object') {
                        return option.id === value.id;
                      }
                      return false;
                    }}
                    filterOptions={options => options} // Disable client-side filtering since we're doing server-side
                    value={selectedCompanyObject || newCompanyForm.company_name}
                    onInputChange={(event, newInputValue) => {
                      handleFormChange('company_name', newInputValue);
                      if (event && event.type === 'change') {
                        setSelectedCompanyObject(null);
                      }
                      handleCompanySearchChange(newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      if (typeof newValue === 'object' && newValue !== null) {
                        setSelectedCompanyObject(newValue);
                        handleFormChange(
                          'company_name',
                          newValue.company_name || newValue.name || ''
                        );
                        if (newValue.latest_revenue) {
                          handleFormChange('revenue', newValue.latest_revenue);
                        }
                      } else {
                        setSelectedCompanyObject(null);
                        handleFormChange('company_name', newValue || '');
                      }
                    }}
                    loading={companySearchLoading}
                    renderInput={params => (
                      <AtomTextField
                        {...params}
                        label="Company Name"
                        error={!!formErrors.company_name}
                        helperText={formErrors.company_name}
                        required
                        placeholder="Start typing company name..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {companySearchLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>
                            {option.company_name || option.name}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: '#666' }}>
                            {[
                              option.region_headquartered &&
                                `Region: ${option.region_headquartered}`,
                              option.currency && `Currency: ${option.currency}`,
                              option.number_of_studies !== undefined &&
                                `Studies: ${option.number_of_studies}`,
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </div>
                        </div>
                      </li>
                    )}
                    noOptionsText={
                      newCompanyForm.company_name.length < 1
                        ? 'Type at least 1 character to search'
                        : 'No companies found - you can create a new one'
                    }
                  />
                </div>

                <div className={companyStyles.formRow}>
                  <div className={companyStyles.formGroup}>
                    <AtomTextField
                      label="Revenue"
                      type="number"
                      value={newCompanyForm.revenue}
                      onChange={e => handleFormChange('revenue', Number(e.target.value))}
                      error={!!formErrors.revenue}
                      helperText={formErrors.revenue}
                      placeholder="Enter revenue amount"
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                    />
                  </div>
                  <div className={companyStyles.formGroup}>
                    <AtomTextField
                      label="Units Sold"
                      type="number"
                      value={newCompanyForm.units_sold}
                      onChange={e => handleFormChange('units_sold', Number(e.target.value))}
                      error={!!formErrors.units_sold}
                      helperText={formErrors.units_sold}
                      placeholder="Enter units sold"
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                    />
                  </div>
                </div>

                <div className={companyStyles.formRow}>
                  <div className={companyStyles.formGroup}>
                    <AtomTextField
                      label="Selling Price"
                      type="number"
                      value={newCompanyForm.selling_price}
                      onChange={e => handleFormChange('selling_price', Number(e.target.value))}
                      error={!!formErrors.selling_price}
                      helperText={formErrors.selling_price}
                      placeholder="Enter selling price"
                      InputProps={{
                        inputProps: { min: 0 },
                      }}
                    />
                  </div>
                  <div className={companyStyles.formGroup}>
                    <AtomTextField
                      select
                      label="Revenue Status"
                      value={newCompanyForm.revenue_status}
                      onChange={e => handleFormChange('revenue_status', e.target.value)}
                    >
                      <MenuItem value="modelled">Modelled</MenuItem>
                      <MenuItem value="revised">Revised</MenuItem>
                      <MenuItem value="override">Override</MenuItem>
                      <MenuItem value="supplier input">Supplier Input</MenuItem>
                    </AtomTextField>
                  </div>
                </div>

                <div className={companyStyles.formRow}>
                  <div className={companyStyles.formGroup}>
                    <AtomTextField
                      select
                      label="Supplier Status"
                      value={newCompanyForm.supplier_status}
                      onChange={e => handleFormChange('supplier_status', e.target.value)}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                    </AtomTextField>
                  </div>
                  <div className={companyStyles.formGroup}>
                    {/* Empty div to maintain grid layout */}
                  </div>
                </div>

                {/* <div className={companyStyles.formRow}>
                  <AtomTextField
                    label="Note"
                    multiline
                    rows={3}
                    value={newCompanyForm.note}
                    onChange={e => handleFormChange('note', e.target.value)}
                    placeholder="Optional notes about the company..."
                  />
                </div> */}
              </div>
            </div>
          </OrgDialog>
        </div>
      )}
    </div>
  );
};

export default CompanyAnalysis;
